import { useState, useEffect, useRef } from "react";
import toast from "../../../utils/toast";
import {
  getNotificationTemplateWhatsApp,
  getNotificationTemplateEvent,
  getNotificationTemplateEventVariable,
  getNotificationTemplateById,
  insertUpdateNotificationTemplate,
  deleteNotificationTemplate,
  toggleNotificationTemplate,
  getCommunicationSettingsWhatsApp,
  getCommunicationSettingsWhatsAppById,
  insertUpdateCommunicationSettingsWhatsApp,
  deleteCommunicationSettingsWhatsAppById,
  toggleCommunicationSettingsWhatsAppById,
  getReplacementVariableforSmsAndWhatsapp,
} from "../../../services/propertyOwner/propertyOwnerServices";

export default function OwnerWhatsappApi() {
  const [templateEvents, setTemplateEvents] = useState([]);
  const [waTemplates, setWaTemplates] = useState([]);
  const [waTemplatesLoading, setWaTemplatesLoading] = useState(true);
  const [waTemplateVariables, setWaTemplateVariables] = useState([]);
  const [deleteWaTplId, setDeleteWaTplId] = useState(null);
  const [deleteWaTplLoading, setDeleteWaTplLoading] = useState(false);
  const [previewWaTpl, setPreviewWaTpl] = useState(null);

  // Add WA Template
  const [addWaTplOpen, setAddWaTplOpen] = useState(false);
  const [addWaTplForm, setAddWaTplForm] = useState({
    eventId: "",
    templateName: "",
    bodyText: "",
    isActive: true,
    sortOrder: 0,
  });
  const [addWaTplErrors, setAddWaTplErrors] = useState({});
  const [addWaTplLoading, setAddWaTplLoading] = useState(false);

  // Edit WA Template
  const [editWaTplOpen, setEditWaTplOpen] = useState(false);
  const [editWaTplId, setEditWaTplId] = useState(null);
  const [editWaTplForm, setEditWaTplForm] = useState({});
  const [editWaTplErrors, setEditWaTplErrors] = useState({});
  const [editWaTplLoading, setEditWaTplLoading] = useState(false);
  const [editWaTplFetching, setEditWaTplFetching] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [waApiList, setWaApiList] = useState([]);
  const [waApiLoading, setWaApiLoading] = useState(true);
  const [systemVars, setSystemVars] = useState([]);
  const [apiTogglingId, setApiTogglingId] = useState(null);
  const [deleteApiId, setDeleteApiId] = useState(null);
  const [deleteApiLoading, setDeleteApiLoading] = useState(false);

  const [addApiOpen, setAddApiOpen] = useState(false);
  const [addApiForm, setAddApiForm] = useState({
    name: "",
    apiUrl: "",
    methodType: "POST",
    responseType: "JSON",
    headers: [{ key: "", value: "" }],
    scheduleDate: "",
    scheduleTime: "",
    isActive: true,
    sortOrder: 0,
  });
  const [addApiErrors, setAddApiErrors] = useState({});
  const [addApiLoading, setAddApiLoading] = useState(false);
  const addBodyRef = useRef(null);

  const [editApiOpen, setEditApiOpen] = useState(false);
  const [editApiId, setEditApiId] = useState(null);
  const [editApiForm, setEditApiForm] = useState({});
  const [editApiErrors, setEditApiErrors] = useState({});
  const [editApiLoading, setEditApiLoading] = useState(false);
  const [editApiFetching, setEditApiFetching] = useState(false);
  const editBodyRef = useRef(null);

  useEffect(() => {
    fetchWaTemplates();
    fetchWaApiList();
  }, []);

  useEffect(() => {
    if (
      !editApiFetching &&
      editApiOpen &&
      editBodyRef.current &&
      editApiForm.requestBodyJson !== undefined
    ) {
      editBodyRef.current.value = editApiForm.requestBodyJson || "";
    }
  }, [editApiFetching]);

  const fetchTemplateEvents = async () => {
    try {
      const res = await getNotificationTemplateEvent();
      if (res.statusCode === 1) setTemplateEvents(res.data || []);
    } catch {
      console.log("Failed to load events");
    }
  };

  const fetchWaTemplates = async () => {
    setWaTemplatesLoading(true);
    try {
      const res = await getNotificationTemplateWhatsApp();
      if (res.statusCode === 1) setWaTemplates(res.data || []);
    } catch {
      console.log("Failed to load WhatsApp templates");
    } finally {
      setWaTemplatesLoading(false);
    }
  };

  const fetchWaVariables = async (eventId) => {
    if (!eventId) {
      setWaTemplateVariables([]);
      return;
    }
    try {
      const res = await getNotificationTemplateEventVariable(eventId);
      if (res.statusCode === 1) setWaTemplateVariables(res.data || []);
    } catch {
      console.log("Failed to load variables");
    }
  };

  // ── Add WA Template ──
  const openAddWaTplModal = () => {
    setAddWaTplForm({
      eventId: "",
      templateName: "",
      bodyText: "",
      isActive: true,
      sortOrder: 0,
    });
    setAddWaTplErrors({});
    setWaTemplateVariables([]);
    fetchTemplateEvents();
    setAddWaTplOpen(true);
  };

  const handleAddWaTplEventChange = (eventId) => {
    const selectedEvent = templateEvents.find(
      (ev) => ev.eventId === Number(eventId),
    );
    setAddWaTplForm((p) => ({
      ...p,
      eventId,
      templateName: selectedEvent ? selectedEvent.eventName : "",
    }));
    setAddWaTplErrors((p) => ({ ...p, eventId: "" }));
    fetchWaVariables(eventId);
  };

  const handleAddWaTplSubmit = async () => {
    const e = {};
    if (!addWaTplForm.eventId) e.eventId = "Required";
    if (!addWaTplForm.bodyText.trim()) e.bodyText = "Required";
    if (Object.keys(e).length) {
      setAddWaTplErrors(e);
      return;
    }
    setAddWaTplLoading(true);
    try {
      const payload = {
        id: 0,
        eventId: Number(addWaTplForm.eventId),
        channel: "WhatsApp",
        templateName: addWaTplForm.templateName,
        subject: "",
        bodyText: addWaTplForm.bodyText,
        isHtml: false,
        isActive: addWaTplForm.isActive,
        sortOrder: Number(addWaTplForm.sortOrder),
        extraFieldsJson: "",
      };
      const res = await insertUpdateNotificationTemplate(payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setAddWaTplOpen(false);
        fetchWaTemplates();
      } else {
        toast.error(res.message);
      }
    } catch {
      toast.error("Failed to create template");
    } finally {
      setAddWaTplLoading(false);
    }
  };

  // ── Edit WA Template ──
  const openEditWaTplModal = async (id) => {
    setEditWaTplId(id);
    setEditWaTplErrors({});
    setEditWaTplFetching(true);
    setEditWaTplOpen(true);
    fetchTemplateEvents();
    try {
      const res = await getNotificationTemplateById(id);
      if (res.statusCode === 1) {
        const d = res.data;
        setEditWaTplForm({
          eventId: d.eventId || "",
          templateName: d.templateName || "",
          bodyText: d.bodyText || "",
          isActive: d.isActive,
          sortOrder: d.sortOrder || 0,
        });
        fetchWaVariables(d.eventId);
      } else {
        toast.error(res.message);
        setEditWaTplOpen(false);
      }
    } catch {
      toast.error("Failed to load template");
      setEditWaTplOpen(false);
    } finally {
      setEditWaTplFetching(false);
    }
  };

  const handleEditWaTplEventChange = (eventId) => {
    const selectedEvent = templateEvents.find(
      (ev) => ev.eventId === Number(eventId),
    );
    setEditWaTplForm((p) => ({
      ...p,
      eventId,
      templateName: selectedEvent ? selectedEvent.eventName : p.templateName,
    }));
    fetchWaVariables(eventId);
  };

  const handleEditWaTplSubmit = async () => {
    const e = {};
    if (!editWaTplForm.eventId) e.eventId = "Required";
    if (!editWaTplForm.bodyText.trim()) e.bodyText = "Required";
    if (Object.keys(e).length) {
      setEditWaTplErrors(e);
      return;
    }
    setEditWaTplLoading(true);
    try {
      const payload = {
        id: editWaTplId,
        eventId: Number(editWaTplForm.eventId),
        channel: "WhatsApp",
        templateName: editWaTplForm.templateName,
        subject: "",
        bodyText: editWaTplForm.bodyText,
        isHtml: false,
        isActive: editWaTplForm.isActive,
        sortOrder: Number(editWaTplForm.sortOrder),
        extraFieldsJson: "",
      };
      const res = await insertUpdateNotificationTemplate(payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setEditWaTplOpen(false);
        fetchWaTemplates();
      } else {
        toast.error(res.message);
      }
    } catch {
      toast.error("Failed to update template");
    } finally {
      setEditWaTplLoading(false);
    }
  };

  // ── Delete WA Template ──
  const handleDeleteWaTemplate = async () => {
    setDeleteWaTplLoading(true);
    try {
      const res = await deleteNotificationTemplate(deleteWaTplId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setDeleteWaTplId(null);
        fetchWaTemplates();
      } else {
        toast.error(res.message);
      }
    } catch {
      toast.error("Failed to delete template");
    } finally {
      setDeleteWaTplLoading(false);
    }
  };

  // ── Toggle WA Template ──
  const handleToggleWaTemplate = async (id) => {
    setTogglingId(id);
    try {
      const res = await toggleNotificationTemplate(id);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setWaTemplates((prev) =>
          prev.map((t) =>
            t.templateId === id ? { ...t, isActive: !t.isActive } : t,
          ),
        );
      } else {
        toast.error(res.message);
      }
    } catch {
      console.log("Failed to toggle");
    } finally {
      setTogglingId(null);
    }
  };

  const fetchWaApiList = async () => {
    setWaApiLoading(true);
    try {
      const res = await getCommunicationSettingsWhatsApp();
      if (res.statusCode === 1) setWaApiList(res.data || []);
    } catch {
      console.log("Failed to load WhatsApp API list");
    } finally {
      setWaApiLoading(false);
    }
  };

  const fetchSystemVars = async () => {
    try {
      const res = await getReplacementVariableforSmsAndWhatsapp();
      if (res.statusCode === 1) setSystemVars(res.data || []);
    } catch {
      console.log("Failed to load system vars");
    }
  };

  const parseHeaders = (headersJson) => {
    try {
      const obj = JSON.parse(headersJson || "{}");
      const entries = Object.entries(obj);
      return entries.length > 0
        ? entries.map(([key, value]) => ({ key, value }))
        : [{ key: "", value: "" }];
    } catch {
      return [{ key: "", value: "" }];
    }
  };

  const headersToJson = (headers) => {
    const obj = {};
    headers.forEach(({ key, value }) => {
      if (key.trim()) obj[key.trim()] = value;
    });
    return JSON.stringify(obj);
  };

  const insertVarToBody = (varName, ref) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const val = el.value;
    el.value = val.substring(0, start) + varName + val.substring(end);
    el.selectionStart = el.selectionEnd = start + varName.length;
    el.focus();
  };

  const openAddApiModal = () => {
    setAddApiForm({
      name: "",
      apiUrl: "",
      methodType: "POST",
      responseType: "JSON",
      headers: [{ key: "", value: "" }],
      scheduleDate: "",
      scheduleTime: "",
      isActive: true,
      sortOrder: 0,
    });
    setAddApiErrors({});
    fetchSystemVars();
    setAddApiOpen(true);
    setTimeout(() => {
      if (addBodyRef.current) addBodyRef.current.value = "";
    }, 0);
  };

  const handleAddApiSubmit = async () => {
    const e = {};
    if (!addApiForm.name.trim()) e.name = "Required";
    if (!addApiForm.apiUrl.trim()) e.apiUrl = "Required";
    if (Object.keys(e).length) {
      setAddApiErrors(e);
      return;
    }
    setAddApiLoading(true);
    try {
      const payload = {
        id: 0,
        isWhatsApp: true,
        name: addApiForm.name,
        apiUrl: addApiForm.apiUrl,
        methodType: addApiForm.methodType,
        responseType: addApiForm.responseType,
        headersJson: headersToJson(addApiForm.headers),
        requestBodyJson: addBodyRef.current?.value || "",
        scheduleDate: addApiForm.scheduleDate
          ? new Date(addApiForm.scheduleDate).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : null,
        scheduleTime: addApiForm.scheduleTime || null,
        isActive: addApiForm.isActive,
        sortOrder: Number(addApiForm.sortOrder),
      };
      const res = await insertUpdateCommunicationSettingsWhatsApp(payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setAddApiOpen(false);
        fetchWaApiList();
      } else toast.error(res.message);
    } catch {
      toast.error("Failed to save");
    } finally {
      setAddApiLoading(false);
    }
  };

  const openEditApiModal = async (id) => {
    setEditApiId(id);
    setEditApiErrors({});
    setEditApiFetching(true);
    setEditApiOpen(true);
    fetchSystemVars();
    try {
      const res = await getCommunicationSettingsWhatsAppById(id);
      if (res.statusCode === 1) {
        const d = res.data;
        setEditApiForm({
          name: d.name || "",
          apiUrl: d.apiUrl || "",
          methodType: d.methodType || "POST",
          responseType: d.responseType || "JSON",
          headers: parseHeaders(d.headersJson),
          requestBodyJson: d.requestBodyJson || "",
          scheduleDate: d.scheduleDate
            ? new Date(d.scheduleDate).toISOString().split("T")[0]
            : "",
          scheduleTime: d.scheduleTime ? d.scheduleTime.substring(0, 5) : "",
          isActive: d.isActive,
          sortOrder: d.sortOrder || 0,
        });
      } else {
        toast.error(res.message);
        setEditApiOpen(false);
      }
    } catch {
      toast.error("Failed to load");
      setEditApiOpen(false);
    } finally {
      setEditApiFetching(false);
    }
  };

  const handleEditApiSubmit = async () => {
    const e = {};
    if (!editApiForm.name.trim()) e.name = "Required";
    if (!editApiForm.apiUrl.trim()) e.apiUrl = "Required";
    if (Object.keys(e).length) {
      setEditApiErrors(e);
      return;
    }
    setEditApiLoading(true);
    try {
      const payload = {
        id: editApiId,
        isWhatsApp: true,
        name: editApiForm.name,
        apiUrl: editApiForm.apiUrl,
        methodType: editApiForm.methodType,
        responseType: editApiForm.responseType,
        headersJson: headersToJson(editApiForm.headers),
        requestBodyJson: editBodyRef.current?.value || "",
        scheduleDate: editApiForm.scheduleDate
          ? new Date(editApiForm.scheduleDate).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : null,
        scheduleTime: editApiForm.scheduleTime || null,
        isActive: editApiForm.isActive,
        sortOrder: Number(editApiForm.sortOrder),
      };
      const res = await insertUpdateCommunicationSettingsWhatsApp(payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setEditApiOpen(false);
        fetchWaApiList();
      } else toast.error(res.message);
    } catch {
      toast.error("Failed to update");
    } finally {
      setEditApiLoading(false);
    }
  };

  const handleDeleteApi = async () => {
    setDeleteApiLoading(true);
    try {
      const res = await deleteCommunicationSettingsWhatsAppById(deleteApiId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setDeleteApiId(null);
        fetchWaApiList();
      } else toast.error(res.message);
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDeleteApiLoading(false);
    }
  };

  const handleToggleApi = async (id) => {
    setApiTogglingId(id);
    try {
      const res = await toggleCommunicationSettingsWhatsAppById(id);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setWaApiList((prev) =>
          prev.map((a) =>
            a.smsApiId === id ? { ...a, isActive: !a.isActive } : a,
          ),
        );
      } else toast.error(res.message);
    } catch {
      console.log("Failed to toggle");
    } finally {
      setApiTogglingId(null);
    }
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              WhatsApp API
            </div>
            <div className="page-sub" id="page-sub">
              API Settings › WhatsApp Configuration
            </div>
          </div>
          <div className="topbar-right" id="topbar-right" />
        </div>
        <div className="content" id="content-area">
          <div
            style={{
              gridTemplateColumns: "230px 1fr",
              gap: 16,
              alignItems: "start",
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 14,
                }}
              >
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>
                    💬 WhatsApp API Form
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      marginTop: 2,
                    }}
                  >
                    Configure API endpoints for sending WhatsApp messages
                  </div>
                </div>
                <button className="btn btn-primary" onClick={openAddApiModal}>
                  + Add New API
                </button>
              </div>

              {waApiLoading ? (
                Array.from({ length: 2 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      background: "var(--bg-secondary)",
                      border: "0.5px solid var(--border-light)",
                      borderRadius: "var(--radius-md)",
                      padding: 14,
                      marginBottom: 10,
                    }}
                  >
                    {Array.from({ length: 3 }).map((_, j) => (
                      <div
                        key={j}
                        style={{
                          height: 13,
                          borderRadius: 6,
                          background:
                            "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                          backgroundSize: "200% 100%",
                          animation: "shimmer 1.4s infinite",
                          width: j === 0 ? "40%" : "70%",
                          marginBottom: 8,
                        }}
                      />
                    ))}
                  </div>
                ))
              ) : waApiList.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "24px 0",
                    color: "var(--text-secondary)",
                    fontSize: 13,
                  }}
                >
                  No WhatsApp API configured yet.
                </div>
              ) : (
                waApiList.map((api) => (
                  <div
                    key={api.smsApiId}
                    style={{
                      background: "var(--bg-secondary)",
                      border: "0.5px solid var(--border-light)",
                      borderRadius: "var(--radius-md)",
                      padding: 14,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      marginBottom: 10,
                      opacity: apiTogglingId === api.smsApiId ? 0.6 : 1,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 5,
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 600 }}>
                          {api.name}
                        </span>
                        {api.isDefault && (
                          <span className="badge badge-teal">Default</span>
                        )}
                        <span
                          className={`badge ${api.isActive ? "badge-active" : "badge-gray"}`}
                        >
                          {api.isActive ? "Active" : "Inactive"}
                        </span>
                        <span className="badge badge-info">
                          {api.methodType}
                        </span>
                        <span className="badge badge-gray">
                          {api.responseType}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--text-secondary)",
                          fontFamily: "monospace",
                          wordBreak: "break-all",
                          lineHeight: "1.6",
                        }}
                      >
                        {api.apiUrl}
                      </div>
                      {api.headersJson && api.headersJson !== "{}" && (
                        <div
                          style={{
                            marginTop: 5,
                            fontSize: 10,
                            color: "var(--text-tertiary)",
                          }}
                        >
                          Headers configured
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                      <button
                        className="btn btn-xs"
                        onClick={() => openEditApiModal(api.smsApiId)}
                      >
                        Edit
                      </button>
                      <button
                        className={`btn btn-xs ${api.isActive ? "btn-danger" : "btn-success"}`}
                        onClick={() => handleToggleApi(api.smsApiId)}
                        disabled={apiTogglingId === api.smsApiId}
                      >
                        {api.isActive ? "Deactivate" : "Activate"}
                      </button>

                      <button
                        className="btn btn-xs btn-danger"
                        onClick={() => setDeleteApiId(api.smsApiId)}
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <div className="card">
              <div
                className="flex-between mb12"
                style={{ flexWrap: "wrap", gap: 10 }}
              >
                <div>
                  <div className="card-title" style={{ margin: 0 }}>
                    💬 WhatsApp Templates
                    <span
                      className="badge badge-info"
                      style={{ marginLeft: 6 }}
                    >
                      {waTemplates.length}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      marginTop: 2,
                    }}
                  >
                    Reusable WhatsApp message bodies. Insert variables like{" "}
                    <code
                      style={{
                        fontFamily: "monospace",
                        background: "var(--bg-secondary)",
                        padding: "1px 4px",
                        borderRadius: 3,
                      }}
                    >
                      {"{"}
                      {"{"}tenant_name{"}"}
                      {"}"}
                    </code>{" "}
                    for personalisation.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="text"
                    placeholder="🔍 Search whatsapp templates"
                    defaultValue=""
                    oninput="searchCommTpl('whatsapp',this.value)"
                    style={{
                      fontSize: 12,
                      padding: "6px 10px",
                      width: 200,
                    }}
                  />
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={openAddWaTplModal}
                  >
                    + Create Template
                  </button>
                </div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 200 }}>Name</th>
                      <th>Body Preview</th>
                      <th style={{ width: 140 }}>Last Modified</th>
                      <th style={{ width: 120 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waTemplatesLoading ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 4 }).map((_, j) => (
                            <td key={j}>
                              <div
                                style={{
                                  height: 14,
                                  borderRadius: 6,
                                  background:
                                    "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                                  backgroundSize: "200% 100%",
                                  animation: "shimmer 1.4s infinite",
                                  width: "70%",
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : waTemplates.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          style={{
                            textAlign: "center",
                            color: "var(--text-tertiary)",
                            padding: 24,
                          }}
                        >
                          No WhatsApp templates found.
                        </td>
                      </tr>
                    ) : (
                      waTemplates.map((t) => (
                        <tr key={t.templateId}>
                          <td style={{ fontWeight: 600 }}>{t.templateName}</td>
                          <td
                            style={{
                              fontSize: 11,
                              color: "var(--text-secondary)",
                            }}
                          >
                            {t.bodyText?.substring(0, 80)}…
                          </td>
                          <td
                            style={{
                              fontSize: 11,
                              color: "var(--text-tertiary)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {t.createdAt
                              ? new Date(t.createdAt).toLocaleDateString(
                                  "en-IN",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  },
                                )
                              : "—"}
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <div className="td-actions">
                              <span
                                className="action-icon"
                                onClick={() => setPreviewWaTpl(t)}
                              >
                                <span className="tip">Preview</span>👁
                              </span>
                              <span
                                className="action-icon"
                                onClick={() => openEditWaTplModal(t.templateId)}
                              >
                                <span className="tip">Edit</span>✎
                              </span>
                              <span
                                className="action-icon red"
                                onClick={() => setDeleteWaTplId(t.templateId)}
                              >
                                <span className="tip">Delete</span>🗑
                              </span>
                              <span
                                className="btn btn-xs"
                                title={t.isActive ? "Activate" : "Deactivate"}
                                style={{
                                  background: t.isActive
                                    ? "var(--green-light, #f5bbbb)"
                                    : "var(--bg-secondary)",
                                  color: t.isActive
                                    ? "var(--green, #16a34a)"
                                    : "var(--text-secondary)",
                                  border: `1px solid ${t.isActive ? "var(--green, #16a34a)" : "var(--red, #e65858)"}`,
                                }}
                                onClick={() =>
                                  handleToggleWaTemplate(t.templateId)
                                }
                                disabled={togglingId === t.id}
                              >
                                {t.isActive ? "✓" : "⊗"}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* ── Add WA Template Modal ── */}
        {addWaTplOpen && (
          <div className="modal-overlay open">
            <div className="modal" style={{ width: 680, maxWidth: "98vw" }}>
              <div className="modal-header">
                <div className="modal-title">💬 Create WhatsApp Template</div>
                <button
                  className="modal-close"
                  onClick={() => setAddWaTplOpen(false)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group mb12">
                  <label>Event *</label>
                  <select
                    value={addWaTplForm.eventId}
                    onChange={(e) => handleAddWaTplEventChange(e.target.value)}
                    style={
                      addWaTplErrors.eventId ? { borderColor: "#dc2626" } : {}
                    }
                  >
                    <option value="">— Select Event —</option>
                    {templateEvents.map((ev) => (
                      <option key={ev.eventId} value={ev.eventId}>
                        {ev.eventName}
                      </option>
                    ))}
                  </select>
                  {addWaTplErrors.eventId && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addWaTplErrors.eventId}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>Message Body *</label>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 6,
                      gap: 8,
                    }}
                  >
                    <select
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          setAddWaTplForm((p) => ({
                            ...p,
                            bodyText: p.bodyText + e.target.value,
                          }));
                        }
                      }}
                      style={{
                        fontSize: 11,
                        padding: "4px 6px",
                        width: "auto",
                      }}
                    >
                      <option value="">+ Insert Variable…</option>
                      {waTemplateVariables.map((v) => (
                        <option key={v.variableId} value={v.placeholder}>
                          {v.placeholder}
                        </option>
                      ))}
                    </select>
                    <span
                      style={{ fontSize: 10, color: "var(--text-tertiary)" }}
                    >
                      {addWaTplForm.bodyText.length} / 1024
                    </span>
                  </div>
                  <textarea
                    value={addWaTplForm.bodyText}
                    rows={9}
                    placeholder="Type your WhatsApp message…"
                    maxLength={1024}
                    onChange={(e) => {
                      setAddWaTplForm((p) => ({
                        ...p,
                        bodyText: e.target.value,
                      }));
                      setAddWaTplErrors((p) => ({ ...p, bodyText: "" }));
                    }}
                    style={{
                      width: "100%",
                      fontFamily: "inherit",
                      fontSize: 13,
                      lineHeight: 1.6,
                      resize: "vertical",
                      ...(addWaTplErrors.bodyText
                        ? { borderColor: "#dc2626" }
                        : {}),
                    }}
                  />
                  {addWaTplErrors.bodyText && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addWaTplErrors.bodyText}
                    </span>
                  )}
                  <div
                    style={{
                      fontSize: 10,
                      color: "var(--text-tertiary)",
                      marginTop: 4,
                    }}
                  >
                    WhatsApp formatting: *bold* _italic_ ~strike~. Newlines
                    preserved.
                  </div>
                </div>
                {waTemplateVariables.length > 0 && (
                  <div
                    className="info-box"
                    style={{ marginTop: 12, fontSize: 11 }}
                  >
                    <strong>Available variables:</strong>{" "}
                    {waTemplateVariables.map((v) => (
                      <code
                        key={v.variableId}
                        style={{
                          fontFamily: "monospace",
                          background: "#fff",
                          padding: "1px 5px",
                          borderRadius: 3,
                          border: "0.5px solid var(--border-light)",
                          marginRight: 3,
                        }}
                      >
                        {v.placeholder}
                      </code>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn" onClick={() => setAddWaTplOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleAddWaTplSubmit}
                  disabled={addWaTplLoading}
                >
                  {addWaTplLoading ? "Saving..." : "Create Template"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Edit WA Template Modal ── */}
        {editWaTplOpen && (
          <div className="modal-overlay open">
            <div className="modal" style={{ width: 680, maxWidth: "98vw" }}>
              <div className="modal-header">
                <div className="modal-title">💬 Edit WhatsApp Template</div>
                <button
                  className="modal-close"
                  onClick={() => setEditWaTplOpen(false)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                {editWaTplFetching ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "24px 0",
                      color: "var(--text-secondary)",
                      fontSize: 13,
                    }}
                  >
                    Loading...
                  </div>
                ) : (
                  <>
                    <div className="form-group mb12">
                      <label>Event *</label>
                      <select
                        value={editWaTplForm.eventId}
                        onChange={(e) =>
                          handleEditWaTplEventChange(e.target.value)
                        }
                        style={
                          editWaTplErrors.eventId
                            ? { borderColor: "#dc2626" }
                            : {}
                        }
                      >
                        <option value="">— Select Event —</option>
                        {templateEvents.map((ev) => (
                          <option key={ev.eventId} value={ev.eventId}>
                            {ev.eventName}
                          </option>
                        ))}
                      </select>
                      {editWaTplErrors.eventId && (
                        <span style={{ color: "#dc2626", fontSize: 11 }}>
                          {editWaTplErrors.eventId}
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Message Body *</label>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 6,
                          gap: 8,
                        }}
                      >
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              setEditWaTplForm((p) => ({
                                ...p,
                                bodyText: p.bodyText + e.target.value,
                              }));
                              e.target.value = "";
                            }
                          }}
                          style={{
                            fontSize: 11,
                            padding: "4px 6px",
                            width: "auto",
                          }}
                        >
                          <option value="">+ Insert Variable…</option>
                          {waTemplateVariables.map((v) => (
                            <option key={v.variableId} value={v.placeholder}>
                              {v.placeholder}
                            </option>
                          ))}
                        </select>
                        <span
                          style={{
                            fontSize: 10,
                            color: "var(--text-tertiary)",
                          }}
                        >
                          {(editWaTplForm.bodyText || "").length} / 1024
                        </span>
                      </div>
                      <textarea
                        value={editWaTplForm.bodyText}
                        rows={9}
                        maxLength={1024}
                        onChange={(e) => {
                          setEditWaTplForm((p) => ({
                            ...p,
                            bodyText: e.target.value,
                          }));
                          setEditWaTplErrors((p) => ({ ...p, bodyText: "" }));
                        }}
                        style={{
                          width: "100%",
                          fontFamily: "inherit",
                          fontSize: 13,
                          lineHeight: 1.6,
                          resize: "vertical",
                          ...(editWaTplErrors.bodyText
                            ? { borderColor: "#dc2626" }
                            : {}),
                        }}
                      />
                      {editWaTplErrors.bodyText && (
                        <span style={{ color: "#dc2626", fontSize: 11 }}>
                          {editWaTplErrors.bodyText}
                        </span>
                      )}
                      <div
                        style={{
                          fontSize: 10,
                          color: "var(--text-tertiary)",
                          marginTop: 4,
                        }}
                      >
                        WhatsApp formatting: *bold* _italic_ ~strike~. Newlines
                        preserved.
                      </div>
                    </div>
                    {waTemplateVariables.length > 0 && (
                      <div
                        className="info-box"
                        style={{ marginTop: 12, fontSize: 11 }}
                      >
                        <strong>Available variables:</strong>{" "}
                        {waTemplateVariables.map((v) => (
                          <code
                            key={v.variableId}
                            style={{
                              fontFamily: "monospace",
                              background: "#fff",
                              padding: "1px 5px",
                              borderRadius: 3,
                              border: "0.5px solid var(--border-light)",
                              marginRight: 3,
                            }}
                          >
                            {v.placeholder}
                          </code>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              {!editWaTplFetching && (
                <div className="modal-footer">
                  <button
                    className="btn"
                    onClick={() => setEditWaTplOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleEditWaTplSubmit}
                    disabled={editWaTplLoading}
                  >
                    {editWaTplLoading ? "Updating..." : "Update Template"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Delete WA Template Modal ── */}
        {deleteWaTplId && (
          <div className="modal-overlay open">
            <div className="modal" style={{ width: 400, maxWidth: "98vw" }}>
              <div className="modal-header">
                <div className="modal-title">Delete WhatsApp Template</div>
                <button
                  className="modal-close"
                  onClick={() => setDeleteWaTplId(null)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <div style={{ fontSize: 14, marginBottom: 8 }}>
                  Are you sure you want to delete this WhatsApp template?
                </div>
                <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                  This action cannot be undone.
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn" onClick={() => setDeleteWaTplId(null)}>
                  Cancel
                </button>
                <button
                  className="btn"
                  style={{
                    background: "var(--red, #dc2626)",
                    color: "#fff",
                    border: "none",
                  }}
                  onClick={handleDeleteWaTemplate}
                  disabled={deleteWaTplLoading}
                >
                  {deleteWaTplLoading ? "Deleting..." : "🗑 Yes, Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Preview WA Template Modal ── */}
        {previewWaTpl && (
          <div
            className="modal-overlay open"
            onClick={() => setPreviewWaTpl(null)}
          >
            <div
              className="modal"
              style={{ width: 560, maxWidth: "98vw" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="modal-header"
                style={{
                  background: "#25d366",
                  borderRadius: "var(--radius-md) var(--radius-md) 0 0",
                }}
              >
                <div className="modal-title" style={{ color: "#fff" }}>
                  💬 WhatsApp Preview — {previewWaTpl.templateName}
                </div>
                <button
                  className="modal-close"
                  style={{ color: "#fff" }}
                  onClick={() => setPreviewWaTpl(null)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body" style={{ padding: 0 }}>
                <div
                  style={{ padding: 20, background: "#e5ddd5", minHeight: 200 }}
                >
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: "0 8px 8px 8px",
                      padding: "10px 14px",
                      maxWidth: "85%",
                      fontSize: 13,
                      lineHeight: 1.7,
                      whiteSpace: "pre-wrap",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    }}
                  >
                    {previewWaTpl.bodyText}
                  </div>
                </div>
                <div
                  style={{
                    padding: "10px 20px",
                    background: "#f9fafb",
                    borderTop: "0.5px solid var(--border-light)",
                    fontSize: 11,
                    color: "var(--text-tertiary)",
                    textAlign: "center",
                  }}
                >
                  Preview uses sample values for variables. Real messages
                  substitute live tenant/owner data.
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn" onClick={() => setPreviewWaTpl(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Add API Modal ── */}
        {addApiOpen && (
          <div className="modal-overlay open">
            <div className="modal" style={{ width: 900, maxWidth: "98vw" }}>
              <div className="modal-header">
                <div className="modal-title">New WhatsApp API</div>
                <button
                  className="modal-close"
                  onClick={() => setAddApiOpen(false)}
                >
                  ✕
                </button>
              </div>
              <div
                className="modal-body"
                style={{
                  display: "grid",
                  gridTemplateColumns: "200px 1fr",
                  gap: 0,
                  padding: 0,
                  minHeight: 500,
                }}
              >
                <div
                  style={{
                    background: "#0F6E56",
                    borderRadius: "0 0 0 var(--radius-lg)",
                    padding: 14,
                    overflowY: "auto",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#fff",
                      marginBottom: 3,
                    }}
                  >
                    Replacement Values
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: "rgba(255,255,255,0.6)",
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      marginBottom: 10,
                    }}
                  >
                    Click to insert into body
                  </div>
                  {systemVars.map((v) => (
                    <div
                      key={v.varId}
                      onClick={() => insertVarToBody(v.varName, addBodyRef)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background: "rgba(0,0,0,0.14)",
                        margin: "2px 0",
                        borderRadius: 5,
                        padding: "7px 10px",
                        cursor: "pointer",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#fff",
                          fontFamily: "monospace",
                        }}
                      >
                        {v.varName}
                      </span>
                      <span
                        style={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}
                      >
                        📋
                      </span>
                    </div>
                  ))}
                </div>
                <div style={{ padding: 20, overflowY: "auto" }}>
                  <div className="form-group mb12">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={addApiForm.name}
                      placeholder="e.g. Meta WhatsApp Cloud"
                      onChange={(e) => {
                        setAddApiForm((p) => ({ ...p, name: e.target.value }));
                        setAddApiErrors((p) => ({ ...p, name: "" }));
                      }}
                      style={
                        addApiErrors.name ? { borderColor: "#dc2626" } : {}
                      }
                    />
                    {addApiErrors.name && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>
                        {addApiErrors.name}
                      </span>
                    )}
                  </div>
                  <div className="form-group mb12">
                    <label>API URL *</label>
                    <textarea
                      value={addApiForm.apiUrl}
                      rows={2}
                      placeholder="Enter full API URL..."
                      style={{
                        minHeight: 52,
                        fontFamily: "monospace",
                        fontSize: 12,
                        lineHeight: "1.6",
                        width: "100%",
                        ...(addApiErrors.apiUrl
                          ? { borderColor: "#dc2626" }
                          : {}),
                      }}
                      onChange={(e) => {
                        setAddApiForm((p) => ({
                          ...p,
                          apiUrl: e.target.value,
                        }));
                        setAddApiErrors((p) => ({ ...p, apiUrl: "" }));
                      }}
                    />
                    {addApiErrors.apiUrl && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>
                        {addApiErrors.apiUrl}
                      </span>
                    )}
                  </div>
                  <div className="form-grid mb12">
                    <div className="form-group">
                      <label>Method Type</label>
                      <select
                        value={addApiForm.methodType}
                        onChange={(e) =>
                          setAddApiForm((p) => ({
                            ...p,
                            methodType: e.target.value,
                          }))
                        }
                      >
                        <option>POST</option>
                        <option>GET</option>
                        <option>PUT</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Response Type</label>
                      <select
                        value={addApiForm.responseType}
                        onChange={(e) =>
                          setAddApiForm((p) => ({
                            ...p,
                            responseType: e.target.value,
                          }))
                        }
                      >
                        <option>JSON</option>
                        <option>XML</option>
                        <option>Text</option>
                      </select>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <label
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--text-secondary)",
                      }}
                    >
                      Dynamic Request Headers
                    </label>
                    <button
                      className="btn btn-xs"
                      onClick={() =>
                        setAddApiForm((p) => ({
                          ...p,
                          headers: [...p.headers, { key: "", value: "" }],
                        }))
                      }
                    >
                      + Add Header
                    </button>
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    {addApiForm.headers.map((h, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 30px",
                          gap: 6,
                          marginBottom: 6,
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="text"
                          value={h.key}
                          placeholder="Header Key e.g. Authorization"
                          style={{ fontSize: 12, fontFamily: "monospace" }}
                          onChange={(e) =>
                            setAddApiForm((p) => {
                              const arr = [...p.headers];
                              arr[idx] = { ...arr[idx], key: e.target.value };
                              return { ...p, headers: arr };
                            })
                          }
                        />
                        <input
                          type="text"
                          value={h.value}
                          placeholder="Value e.g. Bearer YOUR_TOKEN"
                          style={{ fontSize: 12, fontFamily: "monospace" }}
                          onChange={(e) =>
                            setAddApiForm((p) => {
                              const arr = [...p.headers];
                              arr[idx] = { ...arr[idx], value: e.target.value };
                              return { ...p, headers: arr };
                            })
                          }
                        />
                        <button
                          onClick={() =>
                            setAddApiForm((p) => ({
                              ...p,
                              headers: p.headers.filter((_, i) => i !== idx),
                            }))
                          }
                          style={{
                            width: 28,
                            height: 28,
                            border: "0.5px solid #F09595",
                            background: "var(--red-light, #fef2f2)",
                            color: "var(--red, #dc2626)",
                            borderRadius: 5,
                            cursor: "pointer",
                            fontSize: 15,
                            fontFamily: "inherit",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <label
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--text-secondary)",
                    }}
                  >
                    Request Body (JSON)
                  </label>
                  <textarea
                    ref={addBodyRef}
                    rows={7}
                    placeholder='{"messaging_product":"whatsapp","to":"{{WhatsappNo}}","text":{"body":"{{MstTEXT}}"}}'
                    style={{
                      width: "100%",
                      minHeight: 150,
                      fontFamily: "monospace",
                      fontSize: 12,
                      lineHeight: "1.6",
                      background: "#1a1a18",
                      color: "#7dd3fc",
                      borderColor: "#333",
                      marginTop: 6,
                    }}
                  />
                  <div
                    style={{
                      fontSize: 10,
                      color: "var(--text-tertiary)",
                      marginTop: 4,
                    }}
                  >
                    Click any replacement variable on the left to insert at
                    cursor
                  </div>
                  <div
                    style={{
                      marginTop: 14,
                      paddingTop: 12,
                      borderTop: "0.5px solid var(--border-light)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--text-secondary)",
                        marginBottom: 8,
                      }}
                    >
                      Schedule
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Send Date</label>
                        <input
                          type="date"
                          value={addApiForm.scheduleDate}
                          onChange={(e) =>
                            setAddApiForm((p) => ({
                              ...p,
                              scheduleDate: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Send Time</label>
                        <input
                          type="time"
                          value={addApiForm.scheduleTime}
                          onChange={(e) =>
                            setAddApiForm((p) => ({
                              ...p,
                              scheduleTime: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="input-hint" style={{ marginTop: 4 }}>
                      Leave empty to send immediately when triggered
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          fontSize: 13,
                          cursor: "pointer",
                        }}
                      >
                        <label className="toggle">
                          <input
                            type="checkbox"
                            checked={addApiForm.isActive}
                            onChange={(e) =>
                              setAddApiForm((p) => ({
                                ...p,
                                isActive: e.target.checked,
                              }))
                            }
                          />
                          <span className="toggle-slider" />
                        </label>
                        Active
                      </label>
                    </div>
                    <div className="modal-footer">
                      <button
                        className="btn"
                        onClick={() => setAddApiOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleAddApiSubmit}
                        disabled={addApiLoading}
                      >
                        {addApiLoading ? "Saving..." : "Save API Configuration"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Edit API Modal ── */}
        {editApiOpen && (
          <div className="modal-overlay open">
            <div className="modal" style={{ width: 900, maxWidth: "98vw" }}>
              <div className="modal-header">
                <div className="modal-title">Edit WhatsApp API</div>
                <button
                  className="modal-close"
                  onClick={() => setEditApiOpen(false)}
                >
                  ✕
                </button>
              </div>
              {editApiFetching ? (
                <div
                  style={{
                    padding: 40,
                    textAlign: "center",
                    color: "var(--text-secondary)",
                  }}
                >
                  Loading...
                </div>
              ) : (
                <div
                  className="modal-body"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "200px 1fr",
                    gap: 0,
                    padding: 0,
                    minHeight: 500,
                  }}
                >
                  <div
                    style={{
                      background: "#0F6E56",
                      borderRadius: "0 0 0 var(--radius-lg)",
                      padding: 14,
                      overflowY: "auto",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#fff",
                        marginBottom: 3,
                      }}
                    >
                      Replacement Values
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: "rgba(255,255,255,0.6)",
                        textTransform: "uppercase",
                        letterSpacing: ".06em",
                        marginBottom: 10,
                      }}
                    >
                      Click to insert into body
                    </div>
                    {systemVars.map((v) => (
                      <div
                        key={v.varId}
                        onClick={() => insertVarToBody(v.varName, editBodyRef)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          background: "rgba(0,0,0,0.14)",
                          margin: "2px 0",
                          borderRadius: 5,
                          padding: "7px 10px",
                          cursor: "pointer",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#fff",
                            fontFamily: "monospace",
                          }}
                        >
                          {v.varName}
                        </span>
                        <span
                          style={{
                            color: "rgba(255,255,255,0.7)",
                            fontSize: 12,
                          }}
                        >
                          📋
                        </span>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding: 20, overflowY: "auto" }}>
                    <div className="form-group mb12">
                      <label>Name *</label>
                      <input
                        type="text"
                        value={editApiForm.name}
                        onChange={(e) => {
                          setEditApiForm((p) => ({
                            ...p,
                            name: e.target.value,
                          }));
                          setEditApiErrors((p) => ({ ...p, name: "" }));
                        }}
                        style={
                          editApiErrors.name ? { borderColor: "#dc2626" } : {}
                        }
                      />
                      {editApiErrors.name && (
                        <span style={{ color: "#dc2626", fontSize: 11 }}>
                          {editApiErrors.name}
                        </span>
                      )}
                    </div>
                    <div className="form-group mb12">
                      <label>API URL *</label>
                      <textarea
                        value={editApiForm.apiUrl}
                        rows={2}
                        style={{
                          minHeight: 52,
                          fontFamily: "monospace",
                          fontSize: 12,
                          lineHeight: "1.6",
                          width: "100%",
                          ...(editApiErrors.apiUrl
                            ? { borderColor: "#dc2626" }
                            : {}),
                        }}
                        onChange={(e) => {
                          setEditApiForm((p) => ({
                            ...p,
                            apiUrl: e.target.value,
                          }));
                          setEditApiErrors((p) => ({ ...p, apiUrl: "" }));
                        }}
                      />
                      {editApiErrors.apiUrl && (
                        <span style={{ color: "#dc2626", fontSize: 11 }}>
                          {editApiErrors.apiUrl}
                        </span>
                      )}
                    </div>
                    <div className="form-grid mb12">
                      <div className="form-group">
                        <label>Method Type</label>
                        <select
                          value={editApiForm.methodType}
                          onChange={(e) =>
                            setEditApiForm((p) => ({
                              ...p,
                              methodType: e.target.value,
                            }))
                          }
                        >
                          <option>POST</option>
                          <option>GET</option>
                          <option>PUT</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Response Type</label>
                        <select
                          value={editApiForm.responseType}
                          onChange={(e) =>
                            setEditApiForm((p) => ({
                              ...p,
                              responseType: e.target.value,
                            }))
                          }
                        >
                          <option>JSON</option>
                          <option>XML</option>
                          <option>Text</option>
                        </select>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 8,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "var(--text-secondary)",
                        }}
                      >
                        Dynamic Request Headers
                      </label>
                      <button
                        className="btn btn-xs"
                        onClick={() =>
                          setEditApiForm((p) => ({
                            ...p,
                            headers: [...p.headers, { key: "", value: "" }],
                          }))
                        }
                      >
                        + Add Header
                      </button>
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      {(editApiForm.headers || []).map((h, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 30px",
                            gap: 6,
                            marginBottom: 6,
                            alignItems: "center",
                          }}
                        >
                          <input
                            type="text"
                            value={h.key}
                            placeholder="Header Key"
                            style={{ fontSize: 12, fontFamily: "monospace" }}
                            onChange={(e) =>
                              setEditApiForm((p) => {
                                const arr = [...p.headers];
                                arr[idx] = { ...arr[idx], key: e.target.value };
                                return { ...p, headers: arr };
                              })
                            }
                          />
                          <input
                            type="text"
                            value={h.value}
                            placeholder="Header Value"
                            style={{ fontSize: 12, fontFamily: "monospace" }}
                            onChange={(e) =>
                              setEditApiForm((p) => {
                                const arr = [...p.headers];
                                arr[idx] = {
                                  ...arr[idx],
                                  value: e.target.value,
                                };
                                return { ...p, headers: arr };
                              })
                            }
                          />
                          <button
                            onClick={() =>
                              setEditApiForm((p) => ({
                                ...p,
                                headers: p.headers.filter((_, i) => i !== idx),
                              }))
                            }
                            style={{
                              width: 28,
                              height: 28,
                              border: "0.5px solid #F09595",
                              background: "var(--red-light, #fef2f2)",
                              color: "var(--red, #dc2626)",
                              borderRadius: 5,
                              cursor: "pointer",
                              fontSize: 15,
                              fontFamily: "inherit",
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <label
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--text-secondary)",
                      }}
                    >
                      Request Body (JSON)
                    </label>
                    <textarea
                      ref={editBodyRef}
                      rows={7}
                      style={{
                        width: "100%",
                        minHeight: 150,
                        fontFamily: "monospace",
                        fontSize: 12,
                        lineHeight: "1.6",
                        background: "#1a1a18",
                        color: "#7dd3fc",
                        borderColor: "#333",
                        marginTop: 6,
                      }}
                    />
                    <div
                      style={{
                        fontSize: 10,
                        color: "var(--text-tertiary)",
                        marginTop: 4,
                      }}
                    >
                      Click any replacement variable on the left to insert at
                      cursor
                    </div>
                    <div
                      style={{
                        marginTop: 14,
                        paddingTop: 12,
                        borderTop: "0.5px solid var(--border-light)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "var(--text-secondary)",
                          marginBottom: 8,
                        }}
                      >
                        Schedule
                      </div>
                      <div className="form-grid">
                        <div className="form-group">
                          <label>Send Date</label>
                          <input
                            type="date"
                            value={editApiForm.scheduleDate}
                            onChange={(e) =>
                              setEditApiForm((p) => ({
                                ...p,
                                scheduleDate: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label>Send Time</label>
                          <input
                            type="time"
                            value={editApiForm.scheduleTime}
                            onChange={(e) =>
                              setEditApiForm((p) => ({
                                ...p,
                                scheduleTime: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div className="input-hint" style={{ marginTop: 4 }}>
                        Leave empty to send immediately when triggered
                      </div>
                      <div style={{ marginTop: 12 }}>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 13,
                            cursor: "pointer",
                          }}
                        >
                          <label className="toggle">
                            <input
                              type="checkbox"
                              checked={editApiForm.isActive}
                              onChange={(e) =>
                                setEditApiForm((p) => ({
                                  ...p,
                                  isActive: e.target.checked,
                                }))
                              }
                            />
                            <span className="toggle-slider" />
                          </label>
                          Active
                        </label>
                      </div>
                      {!editApiFetching && (
                        <div className="modal-footer">
                          <button
                            className="btn"
                            onClick={() => setEditApiOpen(false)}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={handleEditApiSubmit}
                            disabled={editApiLoading}
                          >
                            {editApiLoading
                              ? "Updating..."
                              : "Update API Configuration"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Delete API Modal ── */}
        {deleteApiId && (
          <div className="modal-overlay open">
            <div className="modal" style={{ width: 400, maxWidth: "98vw" }}>
              <div className="modal-header">
                <div className="modal-title">Delete WhatsApp API</div>
                <button
                  className="modal-close"
                  onClick={() => setDeleteApiId(null)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <div style={{ fontSize: 14, marginBottom: 8 }}>
                  Are you sure you want to delete this WhatsApp API
                  configuration?
                </div>
                <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                  This action cannot be undone.
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn" onClick={() => setDeleteApiId(null)}>
                  Cancel
                </button>
                <button
                  className="btn"
                  style={{
                    background: "var(--red, #dc2626)",
                    color: "#fff",
                    border: "none",
                  }}
                  onClick={handleDeleteApi}
                  disabled={deleteApiLoading}
                >
                  {deleteApiLoading ? "Deleting..." : "🗑 Yes, Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
